import type { SVGProps } from 'react';
import type { ClassTabId } from '@/features/class/types';
import { DEFAULT_CLASS_CODE } from '@/features/class/classCatalog';

export type AppNavIcon = (props: SVGProps<SVGSVGElement>) => JSX.Element;

export interface AppNavItem {
  readonly label: string;
  readonly path: string | null;
  readonly Icon: AppNavIcon;
}

/** Public auth routes (marketing / unauthenticated shell). */
export const AUTH_PATHS = {
  login: '/login',
  register: '/register',
  forgotPassword: '/forgot-password',
} as const;

/** Application route paths (app shell layout). */
export const APP_PATHS = {
  feed: '/feed',
  explore: '/explore',
  hashtagBase: '/feed/tag',
  messages: '/messages',
  notifications: '/notifications',
  calendar: '/calendar',
  payments: '/payments',
  reportCards: '/report-cards',
  profile: '/profile',
  profileEdit: '/profile/edit',
  settings: '/settings',
  settingsArchivedClasses: '/settings/archived-classes',
  settingsBlockedUsers: '/settings/blocked-users',
  settingsNotifications: '/settings/notifications',
  settingsPrivacy: '/settings/privacy',
  settingsAccount: '/settings/account',
  prime: '/prime',
  /** TAP return / order summary for tutor package purchase (legacy path). */
  orderSummary: '/ordersummary',
  orderSummaryEn: '/en/ordersummary',
  /**
   * Legacy Angular tutor package page:
   * `/subscriptions/tutors/:username` (and `/en/subscriptions/tutors/:username`).
   */
  tutorSubscriptions: '/subscriptions/tutors',
  tutorSubscriptionsEn: '/en/subscriptions/tutors',
  classBase: '/class',
  questionsBase: '/questions',
} as const;

export function orderSummaryPath(referenceId: string): string {
  return `${APP_PATHS.orderSummary}/${encodeURIComponent(referenceId)}`;
}

/** Legacy-parity tutor packages page, e.g. `/subscriptions/tutors/hamelalmesk`. */
export function tutorSubscriptionPath(username: string): string {
  return `${APP_PATHS.tutorSubscriptions}/${encodeURIComponent(username.replace(/^@/, ''))}`;
}

export function paymentsPath(tab?: 'enrolled' | 'products' | 'history'): string {
  if (!tab || tab === 'enrolled') return APP_PATHS.payments;
  return `${APP_PATHS.payments}?tab=${tab}`;
}

export function paymentsCartPath(): string {
  return `${APP_PATHS.payments}/cart`;
}

export function paymentsCheckoutPath(): string {
  return `${APP_PATHS.payments}/checkout`;
}

/** Author workspace for reviewing private responses to a question. */
export function questionResponsesPath(questionId: string): string {
  return `${APP_PATHS.questionsBase}/${encodeURIComponent(questionId)}/responses`;
}

/** Hashtag timeline for a caption tag, e.g. `/feed/tag/quantum` */
export function hashtagPath(tag: string): string {
  const normalized = tag.replace(/^#/, '').trim();
  return `${APP_PATHS.hashtagBase}/${encodeURIComponent(normalized)}`;
}

/** Own profile or another user by username, e.g. `/profile/N_Marco` */
export function profilePath(username?: string): string {
  if (!username) {
    return APP_PATHS.profile;
  }
  return `${APP_PATHS.profile}/${username}`;
}

export const DEFAULT_CLASS_TAB: ClassTabId = 'posts';

/** Deep link to a class tab, e.g. `/class/CS101/posts`. */
export function classPath(classCode: string, tab: ClassTabId = DEFAULT_CLASS_TAB): string {
  return `${APP_PATHS.classBase}/${classCode}/${tab}`;
}

/** Default class preview entry (sidebar / redirects). */
export function defaultClassPath(tab: ClassTabId = DEFAULT_CLASS_TAB): string {
  return classPath(DEFAULT_CLASS_CODE, tab);
}

/** @deprecated Use APP_PATHS — kept for incremental migration */
export const SIDEBAR_NAV_PATHS = APP_PATHS;

export function isAppNavActive(pathname: string, itemPath: string | null): boolean {
  if (!itemPath) {
    return false;
  }
  if (itemPath === APP_PATHS.classBase) {
    return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
  }
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

/** True when the sidebar class entry matches the current `/class/:code` route. */
export function isClassNavActive(pathname: string, classCode: string): boolean {
  const prefix = `${APP_PATHS.classBase}/${classCode}`;
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** @deprecated Use isAppNavActive */
export const isSidebarNavActive = isAppNavActive;
